import { create } from 'zustand'

import {
  Condition,
  Deal,
  DealExtraRestrictType,
  DEFAULT_RESTRICT_TYPE,
  getDealSettings,
  isPackageRestricted,
  PackageType,
  StrapiID,
} from '@ca/common/types'
import useSession, {
  getSessionDealContext,
  useCustomer,
  useStaff,
} from 'hooks/useSession'
import { useCallback } from 'react'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import { BundleType } from '@order/orderTypes'
import { usePrescription } from './usePrescription'
import validateCondition from '@order/utils/validateCondition'
import { useDealsById } from './useDeals'
import { useTranslation } from 'react-i18next'
import Analytics from 'hooks/analytics/Analytics'
import { router } from 'expo-router'
import useStartOrder from '@order/hooks/useStartOrder'
import useIntroStore from 'hooks/useIntroStore'
import { getLocalizedDealById } from './useDeal'
import { useIsExtraDeal } from './useIsExtraDeal'
import AppConfig from 'config/AppConfig'

type CbFn = (deal: Deal, packageId?: StrapiID) => void

export type DealTypeModalState = {
  deal?: Deal
  onDealSelect?: CbFn
  bundle?: BundleType
  isOpen: boolean
}

export type DealTypeModalActions = {
  open: (deal: Deal, bundle: BundleType, onSuccess?: CbFn) => void
  select: (packageId: StrapiID) => void
  close: () => void
}

const initialOrderState: DealTypeModalState = {
  isOpen: false,
}

export const useDealTypeModalState = create<
  DealTypeModalState & DealTypeModalActions
>((set, get) => ({
  ...initialOrderState,
  open: (deal, bundle, onSuccess) => {
    set({ deal, bundle, onDealSelect: onSuccess, isOpen: true })
  },
  select: (packageId: StrapiID) => {
    const deal = get().deal
    const onDealSelect = get().onDealSelect
    if (!deal) {
      return
    }
    set({ isOpen: false, onDealSelect: undefined })
    onDealSelect?.(deal, packageId)
  },
  close: () => {
    set({ isOpen: false, onDealSelect: undefined })
  },
}))

export const useDealSelect = () => {
  const setDeal = useSession((s) => s.setDeal)
  const open = useDealTypeModalState((s) => s.open)
  const select = useDealTypeModalState((s) => s.select)
  const config = useRemoteConfig()
  const deals = useDealsById()
  const { i18n } = useTranslation()
  const dealLang = AppConfig.dealLang || i18n.language
  const isExtraDeal = useIsExtraDeal()
  const prescription = usePrescription()

  const updateDeal = useCallback(
    ({
      deal,
      bundle,
      packageId,
    }: {
      packageId?: StrapiID
      deal: Deal
      bundle?: BundleType
    }) => {
      Analytics.logEvent('deal_select', {
        deal_id: `${deal?.id}`,
        deal_code: deal?.code,
        bundle: bundle || 'base',
      })
      setDeal(
        getSessionDealContext(deal, {
          basePackageId: packageId,
        }),
        bundle || 'base'
      )
    },
    [setDeal]
  )

  const clearDeal = useCallback(
    ({ bundle = 'base' }: { bundle?: BundleType }) => {
      setDeal(undefined, bundle)
    },
    [setDeal]
  )

  const selectDeal = useCallback(
    ({
      deal,
      bundle = 'base',
      onSelected,
      type,
      forceRestrictType,
    }: {
      deal?: Deal
      type?: PackageType
      bundle: BundleType
      onSelected?: CbFn
      forceRestrictType?: DealExtraRestrictType
    }) => {
      if (!deal) {
        return
      }

      const validate = (condition: Condition) =>
        validateCondition(
          condition,
          {
            type,
            ref: prescription,
          },
          config?.conditions || {}
        )?.result

      const isExtra = bundle === 'extra' || isExtraDeal

      // In case we don't have a prescription we can't validate the vf condition
      // so we assume always valid

      const vfCondition =
        config?.region.conditions?.vf || config?.region.vfCondition
      const isValidVf =
        isExtra && vfCondition && prescription ? validate(vfCondition) : true

      // If the vf condition is met we take all packages else only sv packages
      const restrictType =
        forceRestrictType ?? deal.extraRestrictType ?? DEFAULT_RESTRICT_TYPE

      const settings = getDealSettings(deal)
      const vfDeal = deal.vfDeal ? deals[deal.vfDeal] : undefined
      const hasVfDeal =
        settings.modalUpsell &&
        isValidVf &&
        !!vfDeal &&
        !isPackageRestricted(type, 'vf', restrictType)

      const shouldFilter = isExtra || !!forceRestrictType
      const filteredPackages = shouldFilter
        ? deal.packages.filter((id) => {
            const pcgk = config?.packages[id]
            if (!pcgk) {
              return false
            }
            const isRestricted = isPackageRestricted(
              type,
              pcgk.type,
              restrictType
            )
            if (isRestricted) {
              return false
            }

            if (!isValidVf && pcgk.type === 'vf') {
              return false
            }
            // Should we have a separate condition for office packages?
            if (!isValidVf && pcgk.type === 'office') {
              return false
            }

            if (pcgk.condition && config.conditions[pcgk.condition]) {
              return validate(config.conditions[pcgk.condition])
            }

            return true
          })
        : deal.packages

      if (filteredPackages.length <= 1 && !hasVfDeal) {
        updateDeal({ deal, bundle, packageId: filteredPackages[0] })
        onSelected?.(deal, filteredPackages[0])
        return
      }

      if (filteredPackages.length === 0 && hasVfDeal && deal.vfDeal) {
        const vfDeal = deals[deal.vfDeal]
        updateDeal({ deal: vfDeal, bundle, packageId: vfDeal.packages[0] })
        onSelected?.(deal, vfDeal.packages[0])
        return
      }

      open(deal, bundle, onSelected)
    },
    [config, prescription, open, updateDeal, isExtraDeal, deals]
  )

  const selectVariant = useCallback(
    (variantId: StrapiID, deal: Deal, bundle: BundleType) => {
      const selectedPackage = deal.packages.find((id) => id === variantId)
      if (!selectedPackage) {
        return
      }
      updateDeal({ deal, packageId: selectedPackage, bundle })
      select(selectedPackage)
    },
    [select, updateDeal]
  )

  const selectUpgrade = useCallback(
    (variantId: StrapiID, upgrade: Deal, bundle?: BundleType) => {
      updateDeal({
        deal: upgrade,
        bundle,
        packageId: variantId,
      })
      select(variantId)
    },
    [select, updateDeal]
  )

  const selectDealById = useCallback(
    (dealId: StrapiID, onSelected?: CbFn) => {
      if (!dealId) return

      const deal = getLocalizedDealById(dealId, dealLang, deals)
      if (!deal) return

      selectDeal({ deal, bundle: 'base', onSelected })
    },
    [deals, dealLang, selectDeal]
  )

  return { selectDeal, selectDealById, selectVariant, selectUpgrade, clearDeal }
}

type Options = {
  type?: PackageType
}

export const useSelectIntroDeal = (options?: Options) => {
  const { selectDeal } = useDealSelect()

  const customer = useCustomer()
  const staff = useStaff()
  const setMode = useIntroStore((s) => s.toggleMode)
  const startOrder = useStartOrder()
  const selectIntroDeal = useCallback(
    (deal: Deal) => {
      selectDeal({
        deal,
        bundle: 'base',
        type: options?.type,
        onSelected: () => {
          if (customer) {
            startOrder()
            return
          }
          if (staff) {
            router.navigate('/staff/panel')
            return
          }
          setMode('staff', { redirect: '/staff/panel' })
        },
      })
    },
    [selectDeal, customer, staff, setMode, startOrder, options?.type]
  )

  return selectIntroDeal
}
